import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import PeriodPicker, { DEFAULT_PERIOD, periodParams } from "@/components/PeriodPicker";
import {
  CalendarDays, Trophy, Euro, Zap, TrendingUp, TrendingDown, PlugZap,
  ArrowRight, Megaphone, AlertTriangle, Turtle, ShoppingCart,
} from "lucide-react";

const eur = (n) => (n == null ? "—" : `€ ${Number(n).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`);

export default function Campaigns() {
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const { data, isLoading } = useQuery({
    queryKey: ["analytics-campaigns", period.key || period.mode],
    queryFn: async () => (await api.get("/analytics/campaigns", { params: periodParams(period) })).data,
  });

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Campagne pubblicitarie</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Per ogni campagna: <strong>quanto hai speso</strong> e <strong>quanto è entrato</strong>. Verde = guadagni, rosso = perdi.
          Le vendite contano per <strong>data di aggiudicazione</strong> nel periodo, anche se il lead è arrivato prima.
        </p>
      </div>

      <PeriodPicker value={period} onChange={setPeriod} testPrefix="campaigns-period" />

      {isLoading ? (
        <>
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </>
      ) : (
        <CampaignsContent data={data} period={period} />
      )}
    </div>
  );
}

function CampaignsContent({ data, period }) {
  const metaOn = data?.meta_connected;
  const campaigns = data?.campaigns || [];
  const totals = data?.totals || {};
  const periodLabel = period.label;

  return (
    <>
      {!metaOn && (
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900" data-testid="meta-pending-banner">
          <PlugZap className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>
            Collega <strong>Meta Ads</strong> nella pagina{" "}
            <Link to="/integrations" className="font-semibold underline">Integrazioni</Link>{" "}
            per vedere anche <strong>quanto spendi</strong> per ogni campagna, il <strong>margine</strong> e il <strong>ROAS</strong>.
            Le classifiche di incasso e velocità qui sotto funzionano già.
          </p>
        </div>
      )}

      {metaOn && <SpendVsRevenueHero totals={totals} periodLabel={periodLabel} />}

      <RevenueByOrigin period={period} periodLabel={periodLabel} />

      <MarginRanking campaigns={campaigns} metaOn={metaOn} periodLabel={periodLabel} />
      <ProductsCard period={period} periodLabel={periodLabel} />
      <CostRanking campaigns={campaigns} metaOn={metaOn} periodLabel={periodLabel} />
      <RevenueRanking campaigns={campaigns} periodLabel={periodLabel} />
      <SpeedRanking campaigns={campaigns} periodLabel={periodLabel} />

      {metaOn && (data?.unmatched_meta || []).length > 0 && (
        <UnmatchedMeta rows={data.unmatched_meta} />
      )}

      {campaigns.length === 0 && (
        <Card className="shadow-sm"><CardContent className="p-8 text-center text-sm text-muted-foreground">
          Nessuna campagna con lead in questo periodo. Prova ad allargare il periodo.
        </CardContent></Card>
      )}
    </>
  );
}

function RevenueByOrigin({ period, periodLabel }) {
  const { data, isLoading } = useQuery({
    queryKey: ["revenue-by-origin", period.key || period.mode],
    queryFn: async () => (await api.get("/analytics/revenue-by-origin", { params: periodParams(period) })).data,
  });
  const rows = data?.by_origin_month || [];
  const noLead = data?.no_lead;
  const maxRev = Math.max(...rows.map((r) => r.revenue), noLead?.revenue || 0, 1);
  return (
    <Card className="border-2 border-slate-200 shadow-sm" data-testid="revenue-by-origin">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-slate-500" />
            <h3 className="font-display text-lg font-bold tracking-tight">Fatturato complessivo — da che mese arriva il lead</h3>
          </div>
          <span className="rounded-full bg-slate-900 px-3 py-1 text-sm font-bold text-white" data-testid="rbo-total">{data?.total_label || "—"}</span>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Tutte le vendite chiuse in {periodLabel} (per data di aggiudicazione), suddivise per il mese in cui è arrivato il lead. Ogni euro è tracciato alla sua origine.
        </p>
        {isLoading ? (
          <Skeleton className="mt-4 h-40 w-full" />
        ) : (
          <div className="mt-4 space-y-2.5">
            {rows.map((r) => (
              <OriginRow key={r.month} label={r.month_label} value={r.revenue} valueLabel={r.revenue_label} sales={r.sales} max={maxRev} tone="emerald" />
            ))}
            {noLead && noLead.revenue > 0 && (
              <OriginRow label="Senza lead (contatto diretto / prima del tracciamento)" value={noLead.revenue} valueLabel={noLead.revenue_label} sales={noLead.sales} max={maxRev} tone="slate" />
            )}
            {rows.length === 0 && (!noLead || !noLead.revenue) && (
              <p className="py-6 text-center text-sm text-muted-foreground">Nessuna vendita in questo periodo.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OriginRow({ label, value, valueLabel, sales, max, tone }) {
  const pct = Math.max(2, Math.round((value / max) * 100));
  const bar = tone === "slate" ? "bg-slate-400" : "bg-emerald-500";
  return (
    <div data-testid="rbo-row">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-slate-700">{label} <span className="text-xs text-muted-foreground">· {sales} vendite</span></span>
        <span className="font-bold tabular-nums text-slate-900">{valueLabel}</span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary">
        <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function SpendVsRevenueHero({ totals, periodLabel }) {
  const gain = (totals.margin ?? 0) >= 0;
  const tone = gain
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : "bg-rose-50 border-rose-200 text-rose-900";
  return (
    <Card className={`border-2 shadow-sm ${tone}`} data-testid="campaigns-hero">
      <CardContent className="p-6">
        <div className="flex items-center gap-2">
          <span className={`h-3 w-3 rounded-full ${gain ? "bg-emerald-500" : "bg-rose-500"}`} />
          <p className="text-base font-bold">
            {gain ? "Le campagne stanno guadagnando 👍" : "Attenzione: le campagne stanno perdendo soldi"}
          </p>
          <span className="ml-auto text-xs font-semibold uppercase tracking-wider opacity-70">{periodLabel}</span>
        </div>
        <div className="mt-5 flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
          <div className="flex-1 rounded-lg bg-white/60 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider opacity-70">Hai speso</p>
            <p className="mt-1 font-display text-4xl font-bold tracking-tight" data-testid="total-spend">{eur(totals.spend)}</p>
          </div>
          <ArrowRight className="hidden h-8 w-8 flex-shrink-0 opacity-50 sm:block" />
          <div className="flex-1 rounded-lg bg-white/60 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider opacity-70">Sono entrati</p>
            <p className="mt-1 font-display text-4xl font-bold tracking-tight" data-testid="total-revenue">{eur(totals.revenue)}</p>
          </div>
          <div className="flex-1 rounded-lg bg-white/60 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider opacity-70">{gain ? "Guadagno" : "Perdita"}</p>
            <p className="mt-1 font-display text-4xl font-bold tracking-tight" data-testid="total-margin">
              {eur(Math.abs(totals.margin ?? 0))}
            </p>
          </div>
        </div>
        {(totals.cost_per_lead != null || totals.cost_per_sale != null) && (
          <p className="mt-4 text-sm opacity-80" data-testid="total-costs">
            In media un <strong>lead</strong> ti costa <strong>{eur(totals.cost_per_lead)}</strong>
            {totals.cost_per_sale != null && <> e una <strong>vendita</strong> ti costa <strong>{eur(totals.cost_per_sale)}</strong></>}.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function RankRow({ pos, best, worst, children }) {
  return (
    <div className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
      best ? "border-emerald-300 bg-emerald-50" : worst ? "border-rose-200 bg-rose-50" : "border-border bg-secondary/30"
    }`}>
      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
        best ? "bg-emerald-500 text-white" : worst ? "bg-rose-500 text-white" : "bg-slate-200 text-slate-700"
      }`}>{pos}</span>
      {best && <Trophy className="h-4 w-4 flex-shrink-0 text-emerald-600" />}
      {children}
    </div>
  );
}

function MarginRanking({ campaigns, metaOn, periodLabel }) {
  const rows = campaigns.filter((c) => c.spend != null).sort((a, b) => (b.margin ?? 0) - (a.margin ?? 0));
  return (
    <Card className="border-2 border-emerald-200 shadow-sm" data-testid="margin-ranking">
      <CardContent className="p-6">
        <div className="mb-1 flex items-center gap-2">
          <Euro className="h-5 w-5 text-emerald-600" />
          <p className="text-sm font-bold">Quale campagna fa guadagnare di più</p>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          Speso vs entrato per ogni campagna ({periodLabel}). Verde = guadagni, rosso = perdi.
        </p>
        {!metaOn ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Collega Meta Ads per vedere la spesa e il guadagno di ogni campagna.
          </p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nessuna campagna Meta abbinata ai lead in questo periodo.
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((c, i) => {
              const gain = (c.margin ?? 0) >= 0;
              return (
                <div key={c.name} data-testid="margin-row">
                  <RankRow pos={i + 1} best={i === 0 && gain} worst={!gain}>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.name}</span>
                    <span className="hidden text-xs text-muted-foreground md:block">
                      Speso <strong>{eur(c.spend)}</strong> → Entrati <strong>{eur(c.revenue)}</strong>
                    </span>
                    {c.roas != null && (
                      <span className="hidden rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 sm:block">
                        ogni €1 → €{c.roas.toLocaleString("it-IT")}
                      </span>
                    )}
                    <span className={`rounded-full px-2.5 py-0.5 text-sm font-bold ${
                      gain ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                    }`}>
                      {gain ? "+" : "−"}{eur(Math.abs(c.margin ?? 0))}
                    </span>
                  </RankRow>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CostRanking({ campaigns, metaOn, periodLabel }) {
  const rows = campaigns
    .filter((c) => c.cost_per_lead != null)
    .sort((a, b) => a.cost_per_lead - b.cost_per_lead);
  return (
    <Card className="shadow-sm" data-testid="cost-ranking">
      <CardContent className="p-6">
        <div className="mb-1 flex items-center gap-2">
          <Euro className="h-5 w-5 text-slate-600" />
          <p className="text-sm font-bold">Quanto ti costa un lead (e una vendita)</p>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          Spesa Meta ÷ lead ricevuti e ÷ vendite chiuse ({periodLabel}). In alto la campagna col lead più economico.
        </p>
        {!metaOn ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Collega Meta Ads per vedere quanto costa un lead e una vendita per ogni campagna.
          </p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nessuna campagna Meta abbinata ai lead in questo periodo.
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((c, i) => (
              <div key={c.name} data-testid="cost-row">
                <RankRow pos={i + 1} best={i === 0} worst={i === rows.length - 1 && rows.length > 1}>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.name}</span>
                  <span className="hidden text-xs text-muted-foreground sm:block">{c.leads} lead · {c.sales} vendite</span>
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-bold text-slate-700">
                    lead {eur(c.cost_per_lead)}
                  </span>
                  <span className={`rounded-full px-2.5 py-0.5 text-sm font-bold ${
                    c.cost_per_sale == null ? "bg-rose-100 text-rose-700" : "bg-violet-100 text-violet-700"
                  }`}>
                    {c.cost_per_sale == null ? "0 vendite" : `vendita ${eur(c.cost_per_sale)}`}
                  </span>
                </RankRow>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProductsCard({ period, periodLabel }) {
  const [showDetail, setShowDetail] = useState(false);
  const { data } = useQuery({
    queryKey: ["analytics-products", period.key || period.mode],
    queryFn: async () => (await api.get("/analytics/products", { params: periodParams(period) })).data,
  });
  if (!data) return null;
  const products = data.products || [];
  const maxSales = Math.max(...products.map((p) => p.sales), 1);
  return (
    <Card className="border-2 border-violet-200 shadow-sm" data-testid="products-card">
      <CardContent className="p-6">
        <div className="mb-1 flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-violet-600" />
          <p className="text-sm font-bold">Cosa comprano davvero</p>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          Dal titolo degli affari <strong>aggiudicati</strong> su Pipedrive ({periodLabel}): spesso chi chiede un corso poi compra altro (es. Abbonamento Premium).
        </p>
        {products.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {data.needs_sync ? "Serve una sincronizzazione Pipedrive per leggere i titoli degli affari." : "Nessuna vendita in questo periodo"}
          </p>
        ) : (
          <>
            <div className="space-y-2">
              {products.map((p, i) => (
                <div key={p.name} data-testid="product-row" className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${i === 0 ? "border-violet-300 bg-violet-50" : "border-border bg-secondary/30"}`}>
                  <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? "bg-violet-500 text-white" : "bg-slate-200 text-slate-700"}`}>{i + 1}</span>
                  {i === 0 && <Trophy className="h-4 w-4 flex-shrink-0 text-violet-600" />}
                  <span className="w-44 min-w-0 flex-shrink-0 truncate text-sm font-medium sm:w-56" title={(p.examples || []).join(" · ")}>{p.name}</span>
                  <div className="hidden h-2 flex-1 overflow-hidden rounded-full bg-slate-100 sm:block">
                    <div className="h-full rounded-full bg-violet-500" style={{ width: `${(p.sales / maxSales) * 100}%` }} />
                  </div>
                  <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-bold text-violet-700">{p.sales} vendite</span>
                  <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-sm font-bold text-emerald-700">{eur(p.revenue)}</span>
                </div>
              ))}
            </div>
            {(data.campaigns || []).length > 0 && (
              <div className="mt-4">
                <button
                  data-testid="toggle-campaign-products"
                  onClick={() => setShowDetail((v) => !v)}
                  className="flex w-full items-center justify-between rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-2.5 text-sm font-semibold text-violet-800 transition-colors hover:bg-violet-100"
                >
                  <span>Cosa ha comprato chi arrivava da ogni campagna</span>
                  <span className="text-xs font-normal underline">{showDetail ? "Nascondi" : "Mostra"}</span>
                </button>
                {showDetail && (
                  <div className="mt-2 space-y-1.5">
                    {data.campaigns.map((c) => (
                      <div key={c.name} data-testid="campaign-products-row" className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-secondary/20 px-3 py-2">
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.name}</span>
                        <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        {c.bought.map((b) => (
                          <span key={b.product} className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700">
                            {b.count} {b.product}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RevenueRanking({ campaigns, periodLabel }) {
  const [showZero, setShowZero] = useState(false);
  const withRev = campaigns.filter((c) => c.revenue > 0).sort((a, b) => b.revenue - a.revenue);
  const zeroRev = campaigns.filter((c) => !c.revenue).sort((a, b) => b.leads - a.leads);
  const max = Math.max(...withRev.map((c) => c.revenue), 1);
  return (
    <Card className="shadow-sm" data-testid="revenue-ranking">
      <CardContent className="p-6">
        <div className="mb-1 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          <p className="text-sm font-bold">Quale campagna rende di più</p>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          Incasso dalle vendite per campagna ({periodLabel}). In alto la migliore, in basso la peggiore.
        </p>
        {withRev.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Nessuna vendita in questo periodo</p>
        ) : (
          <div className="space-y-2">
            {withRev.map((c, i) => (
              <div key={c.name} data-testid="revenue-row">
                <RankRow pos={i + 1} best={i === 0} worst={i === withRev.length - 1 && withRev.length > 1}>
                  <span className="w-40 min-w-0 flex-shrink-0 truncate text-sm font-medium sm:w-56">{c.name}</span>
                  <div className="hidden h-2 flex-1 overflow-hidden rounded-full bg-slate-100 sm:block">
                    <div className="h-full rounded-full bg-blue-500" style={{ width: `${(c.revenue / max) * 100}%` }} />
                  </div>
                  <span className="text-xs text-muted-foreground">{c.sales}/{c.leads} vendite</span>
                  <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-sm font-bold text-blue-700">{eur(c.revenue)}</span>
                </RankRow>
              </div>
            ))}
          </div>
        )}
        {zeroRev.length > 0 && (
          <div className="mt-4">
            <button
              data-testid="toggle-zero-revenue"
              onClick={() => setShowZero((v) => !v)}
              className="flex w-full items-center justify-between rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-800 transition-colors hover:bg-rose-100"
            >
              <span className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />
                {zeroRev.length} campagne senza vendite (€ 0) — rendono di meno
              </span>
              <span className="text-xs font-normal underline">{showZero ? "Nascondi" : "Mostra"}</span>
            </button>
            {showZero && (
              <div className="mt-2 space-y-1.5">
                {zeroRev.map((c) => (
                  <div key={c.name} data-testid="zero-revenue-row" className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 px-3 py-1.5">
                    <span className="min-w-0 flex-1 truncate text-sm">{c.name}</span>
                    <span className="text-xs text-muted-foreground">{c.leads} lead</span>
                    <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-bold text-rose-700">€ 0</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SpeedRanking({ campaigns, periodLabel }) {
  const rows = campaigns
    .filter((c) => c.conv_median_seconds != null)
    .sort((a, b) => a.conv_median_seconds - b.conv_median_seconds);
  const tone = (sec) => (sec <= 86400 ? "bg-emerald-100 text-emerald-700" : sec <= 7 * 86400 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700");
  return (
    <Card className="shadow-sm" data-testid="conversion-speed-ranking">
      <CardContent className="p-6">
        <div className="mb-1 flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" />
          <p className="text-sm font-bold">Quale campagna converte prima</p>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          Tempo tipico dal lead alla vendita ({periodLabel}). In alto la più veloce, in basso la più lenta.
        </p>
        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nessuna vendita in questo periodo: allarga il periodo per vedere i tempi di conversione.
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((c, i) => (
              <div key={c.name} data-testid="speed-row">
                <RankRow pos={i + 1} best={i === 0} worst={i === rows.length - 1 && rows.length > 1}>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.name}</span>
                  {i === rows.length - 1 && rows.length > 1 && <Turtle className="h-4 w-4 flex-shrink-0 text-rose-500" />}
                  <span className="text-xs text-muted-foreground">{c.sales} vendite</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-sm font-bold ${tone(c.conv_median_seconds)}`}>{c.conv_median_label}</span>
                </RankRow>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UnmatchedMeta({ rows }) {
  return (
    <Card className="border-amber-200 bg-amber-50/50 shadow-sm" data-testid="unmatched-meta">
      <CardContent className="p-6">
        <div className="mb-1 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <p className="text-sm font-bold text-amber-900">Spesa Meta senza lead abbinati</p>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          Queste campagne Meta hanno una spesa nel periodo ma nessun lead con lo stesso nome nel gestionale.
        </p>
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.name} className="flex items-center gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2" data-testid="unmatched-row">
              <Megaphone className="h-4 w-4 flex-shrink-0 text-amber-500" />
              <span className="min-w-0 flex-1 truncate text-sm">{r.name}</span>
              <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-sm font-bold text-amber-800">{eur(r.spend)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
